import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Receipt, FileText, Plane, Wallet, PieChart, Award, Settings as SettingsIcon, Lock, ShieldCheck } from 'lucide-react';
import logger from '../../utils/logger';
import FinanceDashboard from './FinanceDashboard';
import RevenueTab from './RevenueTab';
import APTab from './APTab';
import ARTab from './ARTab';
import ReimbursementsTab from './ReimbursementsTab';
import ExpensesTab from './ExpensesTab';
import BudgetsTab from './BudgetsTab';
import GrantsTab from './GrantsTab';
import FinanceSettingsTab from './FinanceSettingsTab';
import ComplianceTab from './ComplianceTab';

function AccessGate({ api, user, onGranted }) {
  const [granting, setGranting] = useState(false);
  const isAdmin = user?.role === 'administrator';
  const selfGrant = async () => {
    setGranting(true);
    try {
      // The /finance/access endpoint only requires admin — any admin can self-grant.
      // Use user.id (from /auth/me) as the target.
      await api.post('/finance/access', { user_id: user.id, grant: true });
      toast.success('Finance access granted. Refreshing…');
      // Refresh /auth/me so the user object reflects finance_access=true
      setTimeout(() => { onGranted?.(); }, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant access');
    }
    setGranting(false);
  };
  return (
    <div className="p-4 sm:p-8 flex items-center justify-center min-h-[60vh]" data-testid="finance-gate">
      <Card className="border-[#DED8EB] bg-white shadow-sm max-w-md w-full">
        <CardContent className="p-6 text-center space-y-4">
          <div className="mx-auto h-14 w-14 rounded-full bg-[#EDE7F6] flex items-center justify-center">
            <Lock className="h-7 w-7 text-[#6B3FA0]" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Finance Portal is locked</h2>
            <p className="text-sm text-[#5A4D6E] mt-2">
              Finance access is explicit for every user — even administrators. This keeps payroll, AP, AR, and reimbursements siloed to approved eyes only.
            </p>
          </div>
          {isAdmin ? (
            <>
              <p className="text-xs text-[#8E85A0]">As an administrator, you can grant yourself access:</p>
              <Button onClick={selfGrant} disabled={granting} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 w-full h-10" data-testid="self-grant-btn">
                {granting ? 'Granting…' : 'Grant Myself Finance Access'}
              </Button>
            </>
          ) : (
            <p className="text-xs text-[#8E85A0]">Ask an administrator to grant you access from Finance → Settings → Manage Access.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function FinancePage() {
  const { api, user, checkAuth } = useAuth();
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [hasAccess, setHasAccess] = useState(!!user?.finance_access);

  useEffect(() => { setHasAccess(!!user?.finance_access); }, [user]);

  const fetchDash = useCallback(async () => {
    if (!user?.finance_access) return;
    try {
      const { data } = await api.get('/finance/dashboard');
      setDash(data);
    } catch (e) { logger.error(e); }
  }, [api, user]);

  useEffect(() => { fetchDash(); }, [fetchDash]);

  const navigateToTab = (key) => {
    const map = { ap: 'ap', ar: 'ar', reimb: 'reimb', expenses: 'expenses', budgets: 'budgets', grants: 'grants', revenue: 'revenue', settings: 'settings' };
    if (map[key]) setTab(map[key]);
  };

  if (!hasAccess) {
    return <AccessGate api={api} user={user} onGranted={async () => {
      await checkAuth?.();
      setHasAccess(true);
    }} />;
  }

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <DollarSign className="h-7 w-7 text-[#6B3FA0]" /> Finance Portal
          </h1>
          <p className="text-xs sm:text-sm text-[#8E85A0] mt-1">Revenue, payables, reimbursements, budgets & grants — one place.</p>
        </div>
        <button onClick={() => setTab('settings')}
          className="text-xs font-semibold text-[#6B3FA0] hover:text-[#5A2F8E] hover:underline flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#DED8EB] bg-white hover:bg-[#F7F4EF] transition-colors"
          data-testid="manage-access-link">
          <SettingsIcon className="h-3.5 w-3.5" /> Manage Access & Approvers
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F0EDF6] border border-[#DED8EB] w-full sm:w-auto overflow-x-auto flex-nowrap justify-start" data-testid="finance-tabs">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-dashboard"><PieChart className="h-3.5 w-3.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-revenue"><TrendingUp className="h-3.5 w-3.5" /> Revenue</TabsTrigger>
          <TabsTrigger value="ap" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-ap"><Receipt className="h-3.5 w-3.5" /> AP</TabsTrigger>
          <TabsTrigger value="ar" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-ar"><FileText className="h-3.5 w-3.5" /> AR</TabsTrigger>
          <TabsTrigger value="reimb" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-reimb"><Plane className="h-3.5 w-3.5" /> Reimbursements</TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-expenses"><Wallet className="h-3.5 w-3.5" /> Expenses</TabsTrigger>
          <TabsTrigger value="budgets" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-budgets"><PieChart className="h-3.5 w-3.5" /> Budgets</TabsTrigger>
          <TabsTrigger value="grants" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-grants"><Award className="h-3.5 w-3.5" /> Grants</TabsTrigger>
          <TabsTrigger value="compliance" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-compliance"><ShieldCheck className="h-3.5 w-3.5" /> Compliance</TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-white shrink-0 gap-1" data-testid="tab-settings"><SettingsIcon className="h-3.5 w-3.5" /> Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-5" data-testid="dashboard-panel">
          <FinanceDashboard api={api} dash={dash} onNavigate={navigateToTab} onRefresh={fetchDash} />
        </TabsContent>

        <TabsContent value="revenue" className="mt-5" data-testid="revenue-panel"><RevenueTab api={api} /></TabsContent>
        <TabsContent value="ap" className="mt-5" data-testid="ap-panel"><APTab api={api} onRefresh={fetchDash} /></TabsContent>
        <TabsContent value="ar" className="mt-5" data-testid="ar-panel"><ARTab api={api} onRefresh={fetchDash} /></TabsContent>
        <TabsContent value="reimb" className="mt-5" data-testid="reimb-panel"><ReimbursementsTab api={api} onRefresh={fetchDash} /></TabsContent>
        <TabsContent value="expenses" className="mt-5" data-testid="expenses-panel"><ExpensesTab api={api} onRefresh={fetchDash} /></TabsContent>
        <TabsContent value="budgets" className="mt-5" data-testid="budgets-panel"><BudgetsTab api={api} /></TabsContent>
        <TabsContent value="grants" className="mt-5" data-testid="grants-panel"><GrantsTab api={api} /></TabsContent>
        <TabsContent value="compliance" className="mt-5" data-testid="compliance-panel"><ComplianceTab api={api} /></TabsContent>
        <TabsContent value="settings" className="mt-5" data-testid="settings-panel"><FinanceSettingsTab api={api} /></TabsContent>
      </Tabs>
    </div>
  );
}
